import React, { useState, useCallback, useMemo } from 'react';
import { ExecutionTemplate } from 'process-pipelines-common';
import { FormPickerComponent } from '@atlas-forms/player-components-react';

export interface RenderOptionsTabProps {
  template: ExecutionTemplate;
  onChange: (updates: Partial<ExecutionTemplate>) => void;
  readOnly?: boolean;
}

export const RenderOptionsTab: React.FC<RenderOptionsTabProps> = ({
  template,
  onChange,
  readOnly = false,
}) => {
  const [showFormPicker, setShowFormPicker] = useState(false);

  const config = useMemo(() => {
    try {
      return JSON.parse(template.configuration || '{}');
    } catch {
      return {};
    }
  }, [template.configuration]);

  const forms = useMemo(() => {
    return config.renderOptions?.forms || [];
  }, [config]);

  const handleAddForm = useCallback(
    (selectedFormId: number | undefined) => {
      if (!selectedFormId) return;

      const newForms = [
        ...forms,
        {
          formId: String(selectedFormId),
          addedAt: new Date().toISOString(),
        },
      ];
      const updated = {
        ...config,
        renderOptions: { ...config.renderOptions, forms: newForms },
      };
      onChange({ configuration: JSON.stringify(updated) });
      setShowFormPicker(false);
    },
    [forms, config, onChange]
  );

  const handleRemoveForm = useCallback(
    (index: number) => {
      const newForms = forms.filter((_: any, i: number) => i !== index);
      const updated = {
        ...config,
        renderOptions: { ...config.renderOptions, forms: newForms },
      };
      onChange({ configuration: JSON.stringify(updated) });
    },
    [forms, config, onChange]
  );

  const handleReorderForm = useCallback(
    (fromIndex: number, toIndex: number) => {
      const newForms = [...forms];
      const [form] = newForms.splice(fromIndex, 1);
      newForms.splice(toIndex, 0, form);
      const updated = {
        ...config,
        renderOptions: { ...config.renderOptions, forms: newForms },
      };
      onChange({ configuration: JSON.stringify(updated) });
    },
    [forms, config, onChange]
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Attached Forms ({forms.length})</h4>
        <button
          onClick={() => setShowFormPicker(true)}
          className="template-editor-webhook-btn"
          style={styles.addButton}
          disabled={readOnly || showFormPicker}
        >
          + Add Form
        </button>
      </div>

      <div style={styles.formsList}>
        {forms.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No forms attached yet.</p>
            <p style={styles.hint}>Add forms to render with this template.</p>
          </div>
        ) : (
          forms.map((form: any, index: number) => (
            <div key={index} style={styles.formCard}>
              <div style={styles.formInfo}>
                <div style={styles.formId}>{form.formId}</div>
                <div style={styles.formDate}>
                  Added: {new Date(form.addedAt).toLocaleDateString()}
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  onClick={() => {
                    if (index > 0) handleReorderForm(index, index - 1);
                  }}
                  style={styles.actionButton}
                  title="Move up"
                  disabled={readOnly || index === 0}
                >
                  ↑
                </button>
                <button
                  onClick={() => {
                    if (index < forms.length - 1) handleReorderForm(index, index + 1);
                  }}
                  style={styles.actionButton}
                  title="Move down"
                  disabled={readOnly || index === forms.length - 1}
                >
                  ↓
                </button>
                <button
                  onClick={() => handleRemoveForm(index)}
                  style={styles.deleteButton}
                  title="Remove"
                  disabled={readOnly}
                >
                  Remove
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showFormPicker && !readOnly && (
        <FormSelectionModal
          onSelect={handleAddForm}
          onClose={() => setShowFormPicker(false)}
        />
      )}
    </div>
  );
};

interface FormSelectionModalProps {
  onSelect: (formId: number | undefined) => void;
  onClose: () => void;
}

const FormSelectionModal: React.FC<FormSelectionModalProps> = ({ onSelect, onClose }) => {
  const [selectedFormId, setSelectedFormId] = useState<number | undefined>(undefined);

  const handleAdd = () => {
    if (selectedFormId) {
      onSelect(selectedFormId);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>Select Form</h3>
          <button onClick={onClose} style={modalStyles.closeButton} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={modalStyles.content}>
          <FormPickerComponent
            control={{ id: 'render-options-form-picker', type: 'form-picker', order: 0, label: 'Form', required: false }}
            value={selectedFormId}
            onChange={setSelectedFormId}
          />
        </div>

        <div style={modalStyles.buttons}>
          <button onClick={onClose} style={modalStyles.cancelButton}>
            Cancel
          </button>
          <button
            onClick={handleAdd}
            style={modalStyles.addButton}
            disabled={!selectedFormId}
          >
            Add Form
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: '#e2e8f0',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
  },
  formsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  emptyState: {
    padding: '32px',
    textAlign: 'center' as const,
    color: '#999',
  },
  hint: {
    margin: '8px 0 0 0',
    fontSize: '12px',
  },
  formCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  formInfo: {
    flex: 1,
  },
  formId: {
    fontWeight: '600',
    fontSize: '13px',
    color: 'var(--text-primary, #e5e7eb)',
    fontFamily: 'monospace',
    marginBottom: '4px',
  },
  formDate: {
    fontSize: '11px',
    color: 'var(--text-secondary, #9ca3af)',
    marginTop: '2px',
  },
  formActions: {
    display: 'flex',
    gap: '4px',
    marginLeft: '12px',
  },
  actionButton: {
    padding: '4px 8px',
    backgroundColor: '#e0e0e0',
    border: '1px solid #999',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '11px',
  },
  deleteButton: {
    padding: '4px 8px',
    backgroundColor: '#ffebee',
    color: '#c33',
    border: '1px solid #c33',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '11px',
  },
} as const;

const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50000,
    backdropFilter: 'blur(2px)',
    minHeight: '100vh',
    minWidth: '100vw',
  },
  modal: {
    backgroundColor: 'var(--modal-bg, rgb(15, 23, 42))',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg, 0 10px 15px rgba(0, 0, 0, 0.4)), 0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.06))',
    width: '100%',
    maxWidth: '500px',
    minWidth: '380px',
    maxHeight: '85vh',
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    color: 'var(--text-primary, #e5e7eb)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border-default, rgba(255, 255, 255, 0.06))',
    flexShrink: 0,
    backgroundColor: 'var(--modal-input-bg, #1e293b)',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary, #e5e7eb)',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: 'var(--text-secondary, #9ca3af)',
    transition: 'color 0.2s ease',
  },
  content: {
    padding: '24px',
    overflowY: 'auto' as const,
    flex: 1,
    backgroundColor: 'var(--modal-bg, rgb(15, 23, 42))',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    padding: '20px 24px',
    borderTop: '1px solid var(--border-default, rgba(255, 255, 255, 0.06))',
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '10px 24px',
    backgroundColor: 'transparent',
    color: 'var(--text-primary, #e5e7eb)',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.12))',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  addButton: {
    padding: '10px 24px',
    backgroundColor: 'var(--primary-color, #30a046)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
} as const;
